// react
import { useRef, useState } from 'react'

// external libs
import { Box, Button, IconButton, makeStyles, Paper, Typography } from '@material-ui/core'
import { Add, DeleteOutlineOutlined, EditOutlined } from '@material-ui/icons'

// components
import ChecklistAnswerList from './ChecklistAnswerList'
import ChecklistItemForm from './ChecklistItemForm'
import Dialog from '../../../control/dialog/dialog'
import SubChecklistList from './SubChecklistList'

// hooks
import { useChecklistContext } from './ChecklistList'
import useAuthenStore from '../../../stores/authen/authenStore'
import useChecklist from '../../../services/um/useChecklist'
import useLoading from '../../../control/loading/useLoading'
import useSnackbarAction from '../../../stores/snackbar/snackbarAction'
import useTranslate from '../../../i18n/useTranslate'

// types
import type { DialogType } from '../../../control/dialog/dialog'
import type { Checklist, ChecklistRequest, DeleteChecklistRequest } from '../../../services/um/useChecklist'

interface ChecklistItemProps {
    topicID: number
    checklist: Checklist
    onChange?: () => void
}

function ChecklistItem(props: ChecklistItemProps) {
    const { topicID, checklist, onChange } = props
    const classes = useStyles()
    const translate = useTranslate()
    const currentLanguage = translate.languageStore.currentLanguage
    const loadingHook = useLoading()
    const snackbarAction = useSnackbarAction()
    const checklistServices = useChecklist()
    const { profile } = useAuthenStore()
    const irb = Number(profile?.irb)
    const { hasEditingChecklist, setEditingChecklistId } = useChecklistContext()

    const [mode, setMode] = useState<'view' | 'edit'>('view')
    const dialogRef = useRef<Record<string, Function>>()
    const [dialogInfo, setDialogInfo] = useState<Partial<DialogType>>()
    const initialValues: ChecklistRequest = {
        nameTH: checklist.nameTH,
        nameEN: checklist.nameEN,
        topicID,
        irb,
        parentID: null,
        isAnswer: false,
        isDefault: false,
        checklistID: checklist.checklistID,
    }
    const [createSubChecklistValues, setCreateSubChecklistValues] = useState<ChecklistRequest | null>(null)
    const [createChecklistAnswerValues, setCreateChecklistAnswerValues] = useState<ChecklistRequest | null>(null)
    const hasSubChecklist = !!checklist.children?.length
    const hasChecklistAnswer = !!checklist.answers?.length

    async function createChecklist(values: ChecklistRequest) {
        try {
            loadingHook.loadingOpen()

            const { status } = await checklistServices.createChecklist(values)

            if (status !== 1) {
                throw new Error(translate.get('MESSAGE.M022'))
            }

            snackbarAction.openSnackbar({
                message: translate.get('ALERT.CONTENT.SAVE_SUCCESS'),
                type: 'success',
            })

            setCreateSubChecklistValues(null)
            setCreateChecklistAnswerValues(null)
            setEditingChecklistId(null)
            onChange && onChange()
        } catch (error: any) {
            snackbarAction.openSnackbar({
                message: error.message,
                type: 'error',
            })
        } finally {
            loadingHook.loadingClose()
        }
    }

    async function updateChecklist(values: ChecklistRequest) {
        try {
            dialogRef.current?.closeDialog()
            loadingHook.loadingOpen()

            const { status } = await checklistServices.updateChecklist(topicID, values)

            if (status !== 1) {
                throw new Error()
            }

            setDialogInfo({
                title: translate.get('ALERT.TITLE.NOTIFICATION'),
                content: translate.get('ALERT.CONTENT.SAVE_SUCCESS'),
                type: 'alert-message',
                onAfterClose: () => onChange && onChange(),
            })

            setEditingChecklistId(null)
            setMode('view')
        } catch (error) {
            setDialogInfo({
                title: translate.get('ALERT.TITLE.NOTIFICATION'),
                content: translate.get('ALERT.CONTENT.SAVE_NOT_SUCCESS'),
                type: 'alert-message',
            })
        } finally {
            loadingHook.loadingClose()
            dialogRef.current?.openDialog()
        }
    }

    function onEdit() {
        setEditingChecklistId(checklist.checklistID)
        setMode('edit')
    }

    async function deleteChecklist() {
        try {
            loadingHook.loadingOpen()

            const payload: DeleteChecklistRequest = {
                checklistID: checklist.checklistID,
                parentID: null,
            }
            const { status } = await checklistServices.deleteChecklist(topicID, payload)

            if (status !== 1) {
                throw new Error()
            }

            setDialogInfo({
                title: translate.get('ALERT.TITLE.NOTIFICATION'),
                content: translate.get('ALERT.CONTENT.DELETE_SUCCESS'),
                type: 'alert-message',
                onAfterClose: () => onChange && onChange(),
            })
        } catch (error) {
            setDialogInfo({
                title: translate.get('ALERT.TITLE.NOTIFICATION'),
                content: translate.get('ALERT.CONTENT.DELETE_UNSUCCESS'),
                type: 'alert-message',
            })
        } finally {
            loadingHook.loadingClose()
            dialogRef.current?.openDialog()
        }
    }

    function onDelete() {
        setDialogInfo({
            title: translate.get('ALERT.TITLE.NOTIFICATION'),
            content: translate.get('ALERT.CONTENT.NEED_TO_DELETE'),
            type: 'alert-confirm',
            onClickConfirm: () => {
                dialogRef.current?.closeDialog()
                deleteChecklist()
            },
        })
        dialogRef.current?.openDialog()
    }

    function onCancel() {
        setEditingChecklistId(null)
        setMode('view')
    }

    function onAddSubChecklist() {
        setCreateSubChecklistValues({
            nameTH: '',
            nameEN: '',
            topicID,
            irb,
            parentID: checklist.checklistID,
            isAnswer: false,
            isDefault: false,
        })
        setEditingChecklistId('add-new-sub-checklist')
    }

    function onCancelAddSubChecklist() {
        setEditingChecklistId(null)
        setCreateSubChecklistValues(null)
    }

    function onAddChecklistAnswer() {
        setCreateChecklistAnswerValues({
            nameTH: '',
            nameEN: '',
            topicID,
            irb,
            parentID: checklist.checklistID,
            isAnswer: true,
            isDefault: !hasChecklistAnswer,
        })
        setEditingChecklistId('add-new-checklist-answer')
    }

    function onCancelAddChecklistAnswer() {
        setEditingChecklistId(null)
        setCreateChecklistAnswerValues(null)
    }

    return (
        <>
            <Paper variant="outlined" className={classes.paper}>
                <Typography className={classes.headline} noWrap variant={'body2'}>
                    {translate.get('LABEL.MAIN_TOPIC')}
                </Typography>

                {/* Checklist Preview */}
                {mode === 'view' ? (
                    <Box className={classes.responsiveFlex} justifyContent="space-between">
                        <Typography className={classes.checklistName} variant={'body2'}>
                            {checklist[`name${currentLanguage.toUpperCase()}` as keyof Checklist]}
                        </Typography>

                        {/* Hide actions if there is a checklist during editing. */}
                        {!hasEditingChecklist ? (
                            <Box display="flex" ml="auto" alignItems="flex-end" flexShrink={0} style={{ gap: 16 }}>
                                <Button
                                    size="large"
                                    variant="outlined"
                                    color="primary"
                                    disabled={hasChecklistAnswer}
                                    startIcon={<Add />}
                                    onClick={onAddSubChecklist}
                                >
                                    <Typography variant="button">{translate.get('LABEL.SUB_TOPIC')}</Typography>
                                </Button>
                                <Button
                                    size="large"
                                    variant="outlined"
                                    color="primary"
                                    disabled={hasSubChecklist}
                                    startIcon={<Add />}
                                    onClick={onAddChecklistAnswer}
                                >
                                    <Typography variant="button"> {translate.get('LABEL.TOPIC_ANSWER')}</Typography>
                                </Button>
                                <IconButton aria-label="edit" color="default" onClick={onEdit}>
                                    <EditOutlined />
                                </IconButton>
                                <IconButton aria-label="delete" color="secondary" onClick={onDelete}>
                                    <DeleteOutlineOutlined />
                                </IconButton>
                            </Box>
                        ) : null}
                    </Box>
                ) : null}

                {/* Checklist Form */}
                {mode === 'edit' ? (
                    <ChecklistItemForm initialValues={initialValues} onSubmit={updateChecklist} onCancel={onCancel} />
                ) : null}
            </Paper>

            {/* SubChecklist List */}
            {checklist.children && checklist.children.length ? (
                <SubChecklistList
                    topicID={topicID}
                    parentID={checklist.checklistID}
                    subChecklistList={checklist.children}
                    onChange={onChange}
                />
            ) : null}

            {/* Answer List */}
            {checklist.answers && checklist.answers.length ? (
                <ChecklistAnswerList
                    topicID={topicID}
                    parentID={checklist.checklistID}
                    checklistAnswerList={checklist.answers}
                    onChange={onChange}
                />
            ) : null}

            {/* SubChecklist Form */}
            {createSubChecklistValues ? (
                <Box pl={10}>
                    <Paper variant="outlined" className={classes.paper}>
                        <Typography className={classes.headline} noWrap variant={'body2'}>
                            {translate.get('LABEL.SUB_TOPIC')}
                        </Typography>

                        <ChecklistItemForm
                            initialValues={createSubChecklistValues}
                            onSubmit={createChecklist}
                            onCancel={onCancelAddSubChecklist}
                        />
                    </Paper>
                </Box>
            ) : null}

            {/* Answer Form */}
            {createChecklistAnswerValues ? (
                <Box pl={10}>
                    <Paper variant="outlined" className={classes.paper}>
                        <Typography className={classes.headline} noWrap variant={'body2'}>
                            {translate.get('LABEL.TOPIC_ANSWER')}
                        </Typography>

                        <ChecklistItemForm
                            initialValues={createChecklistAnswerValues}
                            onSubmit={createChecklist}
                            onCancel={onCancelAddChecklistAnswer}
                        />
                    </Paper>
                </Box>
            ) : null}

            <Dialog onRef={(funcs) => (dialogRef.current = funcs)} {...dialogInfo} />
        </>
    )
}

const useStyles = makeStyles((theme) => ({
    responsiveFlex: {
        display: 'flex',
        flexDirection: 'column',
        gap: 16,
        [theme.breakpoints.up('md')]: {
            flexDirection: 'row',
        },
    },

    paper: {
        padding: 24,
        transition: '0.3s',
        borderRadius: '4px',
        position: 'relative',
        '&:hover': {
            border: `1px solid ${theme.palette.primary.main}`,
        },
    },

    headline: {
        border: 'unset',
        fontWeight: 'bolder',
        fontSize: '16px',
        color: theme.palette.grey[50],
        padding: '5px',
    },

    checklistName: {
        fontWeight: 'lighter',
        fontSize: '20px',
        padding: '5px',
    },
}))

export default ChecklistItem
